import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { rawApi } from '@/apis/rawApi';
import type { UploadedDocumentRow } from '@/utils/document';

type Props = {
  uploads: UploadedDocumentRow[];
};

const kindLabel: Record<UploadedDocumentRow['kind'], string> = {
  profile: 'Profile',
  loan_application: 'Mortgage file',
  legacy: 'Uploaded',
};

export function UploadedDocumentsList({ uploads }: Props) {
  const downloadLoanDoc = async (documentId: string, name: string) => {
    try {
      const res = await rawApi.get(
        `/loan-applications/documents/file/${documentId}/download`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'download';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Uploaded documents
      </Typography>
      {uploads.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No documents uploaded yet. Use Upload on each required item above.
        </Typography>
      ) : (
        <List dense disablePadding>
          {uploads.map((row) => (
            <ListItem
              key={row.id}
              divider
              secondaryAction={
                row.url ? (
                  <IconButton
                    component="a"
                    href={row.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    aria-label={`View ${row.fileName}`}
                  >
                    <i className="fa-light fa-eye" />
                  </IconButton>
                ) : row.loanDocumentId ? (
                  <Button
                    size="small"
                    onClick={() =>
                      void downloadLoanDoc(row.loanDocumentId!, row.fileName)
                    }
                  >
                    Download
                  </Button>
                ) : null
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <i
                  className="fa-light fa-file-lines"
                  style={{ fontSize: 18, opacity: 0.7 }}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {row.fileName}
                    </Typography>
                    <Chip
                      label={kindLabel[row.kind]}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  [
                    row.relatedLabel ? `For: ${row.relatedLabel}` : null,
                    row.uploadedAt
                      ? `Uploaded ${new Date(
                          row.uploadedAt
                        ).toLocaleDateString()}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || undefined
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
